import type { PickupRequest, RequestFilter, RequestKind, RequestStatus } from "./api";
import { knownName } from "./memberNames";
import type { SortColumn } from "./sort";

// See NEED_DAYS and NOTE_MAX in platform/api/app/pickup_requests.py.
export const NEED_DAYS = 7;
export const NOTE_MAX = 500;

export const STATUS_TEXT: Record<RequestStatus, string> = {
  received: "Received",
  scheduled: "Scheduled",
  collected: "Collected",
  cancelled: "Cancelled",
  late: "Late",
};

export const STATUS_ORDER: readonly RequestStatus[] = ["late", "received", "scheduled", "collected", "cancelled"];

export const FILTERS: readonly { value: RequestFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "late", label: "Late" },
  { value: "received", label: "Received" },
  { value: "scheduled", label: "Scheduled" },
  { value: "collected", label: "Collected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "all", label: "All" },
];

export const KIND_TEXT: Record<RequestKind, string> = { pickup: "Pickup", container: "Container", lock: "Lock", other: "Other service" };
export const KIND_ORDER: readonly RequestKind[] = ["pickup", "container", "lock", "other"];
export const WINDOW_TEXT: Record<NonNullable<PickupRequest["time_window"]>, string> = { any: "Any time", morning: "Morning", afternoon: "Afternoon" };

export const kindOf = (request: Pick<PickupRequest, "kind">): RequestKind => request.kind ?? "pickup";
export const windowOf = (request: Pick<PickupRequest, "time_window">) => WINDOW_TEXT[request.time_window ?? "any"] ?? WINDOW_TEXT.any;

/** The kind in the URL, or "all". */
export function readKind(search: string): RequestKind | "all" {
  const value = new URLSearchParams(search).get("kind");
  return KIND_ORDER.some((kind) => kind === value) ? (value as RequestKind) : "all";
}

export const ofKind = (requests: readonly PickupRequest[], kind: RequestKind | "all") =>
  kind === "all" ? [...requests] : requests.filter((request) => kindOf(request) === kind);

export function readFilter(search: string): RequestFilter {
  const value = new URLSearchParams(search).get("status");
  return FILTERS.some((filter) => filter.value === value) ? (value as RequestFilter) : "open";
}

/** The day plus some days, as YYYY-MM-DD. */
export function addDays(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

export const defaultNeedBy = (today: string) => addDays(today, NEED_DAYS);

export type RequestProblem = { field: "need_by" | "note"; text: string };

/** The first problem of the request form, with its field, or null. */
export function requestProblem(needBy: string, note: string, today: string): RequestProblem | null {
  if (!needBy) return { field: "need_by", text: "Enter the need-by day." };
  if (needBy < today) return { field: "need_by", text: "The need-by day cannot be before today." };
  if (needBy > addDays(today, 365)) return { field: "need_by", text: "The need-by day is at most one year ahead." };
  if (note.trim().length > NOTE_MAX) return { field: "note", text: `The note can have ${NOTE_MAX} characters at most.` };
  return null;
}

export const requestError = (needBy: string, note: string, today: string) => requestProblem(needBy, note, today)?.text ?? "";

/** "Scheduled for Oct 22", or the status alone. */
export function statusDetail(request: Pick<PickupRequest, "status" | "scheduled_on">): string {
  const text = STATUS_TEXT[request.status];
  if (request.status !== "scheduled" || !request.scheduled_on) return text;
  return `${text} for ${shortDay(request.scheduled_on)}`;
}

export function shortDay(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
}

/** The ids of the shown requests that the user can cancel. */
export const cancelIds = (requests: readonly PickupRequest[]) => requests.filter((request) => request.can_cancel).map((request) => request.id);

export const requestCount = (count: number) => (count === 1 ? "1 request" : `${count} requests`);

export const cancelText = (count: number) => `Cancel ${requestCount(count)}?`;

export function cancelledText(cancelled: number, unchanged: number): string {
  const done = `Cancelled ${requestCount(cancelled)}.`;
  return unchanged ? `${done} ${unchanged} had no open request.` : done;
}

/** The open request of an account, the soonest need-by day first. */
export function openRequest(requests: readonly PickupRequest[] | null | undefined): PickupRequest | null {
  const open = (requests ?? []).filter((request) => ["received", "scheduled", "late"].includes(request.status));
  open.sort((a, b) => a.need_by.localeCompare(b.need_by));
  return open[0] ?? null;
}

/** The name of a member, or the part of the email before "@" and "+". */
export function personName(value: string): string {
  return value.includes("@") ? (knownName(value) ?? value.split("@")[0].split("+")[0]) : value;
}

export const noteCount = (note: string) => `${note.trim().length} of ${NOTE_MAX} characters`;

export const SOURCE_TEXT: Record<PickupRequest["source"], string> = { screen: "Account page", call: "Call", seed: "Synthetic", portal: "Customer portal" };

/** A portal request has no member: a person of the restaurant sent it. */
export const PORTAL_BY: Partial<Record<PickupRequest["source"], string>> = { portal: "Restaurant" };

/** U-5: "By" and "From" go under the request time, so the default columns fit at 1280 px with the menu open. */
export function requestedBy(request: Pick<PickupRequest, "requested_by" | "source">): string {
  const by = request.requested_by ? personName(request.requested_by) : PORTAL_BY[request.source];
  return [by, SOURCE_TEXT[request.source]].filter(Boolean).join(" · ");
}

export const REQUEST_COLUMNS: SortColumn<PickupRequest>[] = [
  { key: "account", label: "Account", value: (r) => r.account_number },
  { key: "status", label: "Status", value: (r) => r.status, order: STATUS_ORDER },
  { key: "kind", label: "Request", value: (r) => kindOf(r), order: KIND_ORDER },
  { key: "need_by", label: "Need by", value: (r) => r.need_by },
  { key: "window", label: "Time", value: (r) => windowOf(r) },
  { key: "requested", label: "Requested", value: (r) => r.requested_at, first: "desc" },
  { key: "note", label: "Note", value: (r) => r.note },
];

export function statusClass(status: RequestStatus): string {
  if (status === "late") return "chip-flag serious";
  return status === "collected" || status === "cancelled" ? "chip-flag neutral" : "chip-flag";
}
